import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { withSuppressSuccessToast } from '@core/http/api-response.envelope';
import { environment } from '../../../../../../environments/environment';
import { PolicySchemaResponse } from '../models/policy-schema.model';
import {
  ApiResponse,
  PaginatedApiResponse,
} from '@shared/models/api-response.models';
import { LocalizedCode } from '@/app/dashboard/models/dashboard.model';

/**
 * User info from API
 */
export interface UserInfo {
  userId: string;
  firstname: string;
  lastname: string;
  status: string;
}

/**
 * Users data response
 */
export interface UsersData {
  roleCount: {
    owner: number;
    signer: number;
    approver: number;
  };
  owner: UserInfo[];
  signer: UserInfo[];
  approver: UserInfo[];
}

/** `GET /api/v1/users` (policy-management) — users grouped by role. */
export type GetUsersResponse = ApiResponse<UsersData>;

/**
 * Create policy request payload
 */
export interface CreatePolicyRequest {
  domain: string;
  resource: string;
  action: string;
  scopeType?: string;
  scopeId?: string;
  stages: PolicyStageDto[];
  idempotencyKey?: string;
}

/**
 * Policy stage DTO
 */
export interface PolicyStageDto {
  stageNo: number;
  rules: StageRoleRuleDto[];
  ttlMinutes?: number;
  selfApprovalAllowed?: boolean;
}

/**
 * Stage role rule DTO
 */
export interface StageRoleRuleDto {
  role: string;
  verification?: string;
  atLeastFromAll?: number;
  atLeastFromList?: {
    count: number;
    userIds: string[];
  };
  allOfAll?: boolean;
}

/** `POST /api/v1/policies` — created policy / approval request. */
export type CreatePolicyResponse = ApiResponse<{
  policyId?: string;
  approvalRequestId?: string;
}>;

/**
 * Policy details from API.
 *
 * The enum-like fields (`domain`, `resourceType`, `action`, `status`) come
 * back as `LocalizedCode` (`{ value, translation }`) — the same shape used
 * by the policy list and the approval-request endpoints. Consumers should
 * read `.translation` for display and `.value` for routing / comparisons.
 */
export interface PolicyDetails {
  id: string;
  accountId: string;
  domain: LocalizedCode;
  resourceType: LocalizedCode;
  action: LocalizedCode;
  scopeType?: string | null;
  scopeId?: string | null;
  version: number;
  status: LocalizedCode;
  createdById: string;
  creator: {
    id: string;
    firstname: string;
    lastname: string;
  };
  stages: PolicyStageDto[];
  pendingApproval?: any | null;
  createdAt: string;
  updatedAt: string;
  policyAction: {
    value: {
      domain: string;
      action: string;
      resourceType: string;
      scope: string | null;
    };
    translation: string;
  };
}

/** `GET /api/v1/policies/:id`. */
export type GetPolicyByIdResponse = ApiResponse<PolicyDetails>;

/**
 * Edit policy request payload
 */
export interface EditPolicyRequest {
  stages: PolicyStageDto[];
  changeReason?: string;
  idempotencyKey?: string;
}

/** `PATCH /api/v1/policies/:id` — edit policy. */
export type EditPolicyResponse = ApiResponse<{
  policyId?: string;
  version?: number;
  approvalRequestId?: string;
  stageNo?: number;
  expiresAt?: string;
}>;

/**
 * Delete policy request payload
 */
export interface DeletePolicyRequest {
  deleteReason?: string;
  idempotencyKey?: string;
}

/** `DELETE /api/v1/policies/:id` — delete policy. */
export type DeletePolicyResponse = ApiResponse<{
  policyId?: string;
  version?: number;
  approvalRequestId?: string;
  stageNo?: number;
  expiresAt?: string;
}>;

/**
 * Activate policy request payload
 */
export interface ActivatePolicyRequest {
  changeReason?: string;
  idempotencyKey?: string;
}

/**
 * Deactivate policy request payload
 */
export interface DeactivatePolicyRequest {
  changeReason?: string;
  idempotencyKey?: string;
}

/** `POST /api/v1/policies/:id/(activate|deactivate)`. */
export type ActivateDeactivatePolicyResponse = ApiResponse<{
  policyId?: string;
  version?: number;
  approvalRequestId?: string;
  stageNo?: number;
  expiresAt?: string;
}>;

/**
 * Policy version history item
 */
export interface PolicyVersionItem {
  id: string;
  version: number;
  operationType: LocalizedCode;
  changedBy: {
    id: string;
    firstname: string;
    lastname: string;
  };
  changedAt: string;
  changeReason: string | null;
}

/** `GET /api/v1/policies/:id/versions` — paginated version history. */
export type GetPolicyVersionsResponse = PaginatedApiResponse<PolicyVersionItem>;

/**
 * Policy version data for comparison
 */
export interface PolicyVersionData {
  version: number;
  stages: PolicyStageDto[];
  status: string;
  changedBy: {
    id: string;
    firstname: string;
    lastname: string;
  };
  changedAt: string;
  changeReason: string;
}

/** `GET /api/v1/policies/:id/versions/:version` — diff between versions. */
export type GetPolicyVersionComparisonResponse = ApiResponse<{
  policyId: string;
  operationType: string;
  previous: PolicyVersionData;
  current: PolicyVersionData;
}>;

/**
 * Policy Service
 * Handles all HTTP requests related to policy management
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyService {
  private readonly baseUrl = `${environment.policyManagementApiUrl}/api/v1/policies`;
  private readonly usersUrl = `${environment.policyManagementApiUrl}/api/v1/users`;

  constructor(private http: HttpClient) {}

  /**
   * Get policy schema
   * @returns Observable of policy schema response
   */
  getPolicySchema(): Observable<PolicySchemaResponse> {
    return this.http.get<PolicySchemaResponse>(`${this.baseUrl}/schema`);
  }

  /**
   * Get users grouped by role
   * @returns Observable of users response
   */
  getUsers(): Observable<GetUsersResponse> {
    return this.http.get<GetUsersResponse>(this.usersUrl);
  }

  /**
   * Create a new policy
   * @param payload Policy creation payload
   * @returns Observable of create policy response
   */
  createPolicy(payload: CreatePolicyRequest): Observable<CreatePolicyResponse> {
    // The success toast is the caller's: policy-management answers 200 with
    // "created" even when it only queued the policy for approval (F-158).
    return this.http.post<CreatePolicyResponse>(this.baseUrl, payload, {
      context: withSuppressSuccessToast(),
    });
  }

  /**
   * Get policy by ID
   * @param policyId Policy ID
   * @returns Observable of policy details response
   */
  getPolicyById(policyId: string): Observable<GetPolicyByIdResponse> {
    return this.http.get<GetPolicyByIdResponse>(`${this.baseUrl}/${policyId}`);
  }

  /**
   * Edit an existing policy
   * @param policyId Policy ID
   * @param payload Policy edit payload
   * @returns Observable of edit policy response
   */
  editPolicy(
    policyId: string,
    payload: EditPolicyRequest
  ): Observable<EditPolicyResponse> {
    // The success toast is the caller's: policy-management answers 200 with
    // "updated" even when it only queued the edit for approval (F-158).
    return this.http.patch<EditPolicyResponse>(
      `${this.baseUrl}/${policyId}`,
      payload,
      { context: withSuppressSuccessToast() }
    );
  }

  /**
   * Delete an existing policy
   * @param policyId Policy ID
   * @param payload Delete policy payload
   * @returns Observable of delete policy response
   */
  deletePolicy(
    policyId: string,
    payload: DeletePolicyRequest = {}
  ): Observable<DeletePolicyResponse> {
    return this.http.request<DeletePolicyResponse>(
      'DELETE',
      `${this.baseUrl}/${policyId}`,
      {
        body: payload,
      }
    );
  }

  /**
   * Activate an existing policy
   * @param policyId Policy ID
   * @param payload Activate policy payload
   * @returns Observable of activate policy response
   */
  activatePolicy(
    policyId: string,
    payload: ActivatePolicyRequest = {}
  ): Observable<ActivateDeactivatePolicyResponse> {
    return this.http.post<ActivateDeactivatePolicyResponse>(
      `${this.baseUrl}/${policyId}/activate`,
      payload
    );
  }

  /**
   * Deactivate an existing policy
   * @param policyId Policy ID
   * @param payload Deactivate policy payload
   * @returns Observable of deactivate policy response
   */
  deactivatePolicy(
    policyId: string,
    payload: DeactivatePolicyRequest = {}
  ): Observable<ActivateDeactivatePolicyResponse> {
    return this.http.post<ActivateDeactivatePolicyResponse>(
      `${this.baseUrl}/${policyId}/deactivate`,
      payload
    );
  }

  /**
   * Get policy version history
   * @param policyId Policy ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   * @returns Observable of policy versions response
   */
  getPolicyVersions(
    policyId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<GetPolicyVersionsResponse> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('limit', limit.toString());

    return this.http.get<GetPolicyVersionsResponse>(
      `${this.baseUrl}/${policyId}/versions`,
      {
        params,
      }
    );
  }

  /**
   * Get policy version comparison data
   * @param policyId Policy ID
   * @param versionNumber Version number to compare
   * @returns Observable of policy version comparison response
   */
  getPolicyVersionComparison(
    policyId: string,
    versionNumber: number
  ): Observable<GetPolicyVersionComparisonResponse> {
    return this.http.get<GetPolicyVersionComparisonResponse>(
      `${this.baseUrl}/${policyId}/versions/${versionNumber}`
    );
  }
}
